import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// utils
import { matchesSearchText } from '../../../utils/textNormalization';

// styles
import {
    InputDropdownSearchableWrapper,
    InputDropdownSearchableControl,
    InputDropdownSearchableLabel,
    InputDropdownSearchableTrigger,
    InputDropdownSearchableSearchField,
    InputDropdownSearchableIcon,
    InputDropdownSearchablePanel,
    InputDropdownSearchableOption,
    InputDropdownSearchableEmpty,
} from './InputDropdownSearchable.styles';

// icons
import { KeyboardArrowDown } from '@mui/icons-material';

/**
 * Single-select dropdown with a search field, for option lists too long to scan.
 * Same contract as InputDropdown (string options, value, onChange) so call sites can swap.
 * Options are only in the DOM while open, unlike a native select.
 *
 * @param {(string | { value: string, label: string })[]} options Objects let the displayed and
 *   searched text differ from the committed value, e.g. "Gold Refractor /99" for "Gold Refractor".
 * @param {boolean} [portalPanel] Render the option list in a body-level portal. Needed when an
 *   ancestor clips overflow; otherwise leave off so the list stays anchored in flow.
 */
export default function InputDropdownSearchable({
    options = [],
    value,
    onChange,
    id,
    label,
    placeholder = 'Select',
    searchPlaceholder = 'Search...',
    size = 'default',
    portalPanel = false,
    align = 'left',
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0, width: 0 });
    const wrapperRef = useRef(null);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);

    const normalizedOptions = useMemo(() => options.map((option) => (
        option != null && typeof option === 'object'
            ? { value: option.value, label: option.label ?? String(option.value) }
            : { value: option, label: String(option) }
    )), [options]);

    const filteredOptions = useMemo(() => {
        const trimmed = query.trim();
        if (!trimmed) return normalizedOptions;
        return normalizedOptions.filter((option) => matchesSearchText(trimmed, option.label));
    }, [normalizedOptions, query]);

    const close = useCallback(() => {
        setIsOpen(false);
        setQuery('');
    }, []);

    const updatePanelPosition = useCallback(() => {
        const rect = wrapperRef.current?.getBoundingClientRect();
        if (!rect) return;
        const next = { top: rect.bottom + 6, left: rect.left, width: rect.width };
        /* Scrolling the list itself also fires the listener, so skip no-op updates. */
        setPanelPosition((prev) => (
            prev.top === next.top && prev.left === next.left && prev.width === next.width
                ? prev
                : next
        ));
    }, []);

    const open = useCallback(() => {
        if (portalPanel) updatePanelPosition();
        setIsOpen(true);
        setQuery('');
        const selectedIndex = normalizedOptions.findIndex((option) => option.value === value);
        setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }, [normalizedOptions, value, portalPanel, updatePanelPosition]);

    const commit = useCallback((option) => {
        onChange?.(option.value);
        close();
        triggerRef.current?.focus();
    }, [onChange, close]);

    useEffect(() => {
        setActiveIndex(0);
    }, [query]);

    useEffect(() => {
        if (!isOpen) return;
        /* Checks the panel too: when portaled it lives outside the wrapper, and closing on
           its mousedown would unmount the option before its click could land. */
        const handleOutsideClick = (e) => {
            if (wrapperRef.current?.contains(e.target)) return;
            if (panelRef.current?.contains(e.target)) return;
            close();
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isOpen, close]);

    useEffect(() => {
        if (!isOpen || !portalPanel) return;
        window.addEventListener('scroll', updatePanelPosition, true);
        window.addEventListener('resize', updatePanelPosition);
        return () => {
            window.removeEventListener('scroll', updatePanelPosition, true);
            window.removeEventListener('resize', updatePanelPosition);
        };
    }, [isOpen, portalPanel, updatePanelPosition]);

    useEffect(() => {
        if (!isOpen) return;
        panelRef.current
            ?.querySelector(`[data-index="${activeIndex}"]`)
            ?.scrollIntoView({ block: 'nearest' });
    }, [isOpen, activeIndex]);

    /* Handled keys are stopped from bubbling: ancestors on the checklist page act on
       Enter and on single characters, and would otherwise hijack them. */
    const handleTriggerKeyDown = (e) => {
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        e.preventDefault();
        e.stopPropagation();
        open();
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, filteredOptions.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            const option = filteredOptions[activeIndex];
            if (option != null) commit(option);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            close();
            triggerRef.current?.focus();
        } else if (e.key === 'Tab') {
            close();
        }
    };

    const hasValue = value != null && value !== '';
    const selectedLabel = normalizedOptions.find((option) => option.value === value)?.label;

    const panel = isOpen ? (
        <InputDropdownSearchablePanel
            ref={panelRef}
            id={`${id}-listbox`}
            role="listbox"
            $portal={portalPanel}
            style={portalPanel ? {
                top: panelPosition.top,
                left: panelPosition.left,
                minWidth: panelPosition.width,
            } : undefined}
        >
            {filteredOptions.length === 0 ? (
                <InputDropdownSearchableEmpty>No matches</InputDropdownSearchableEmpty>
            ) : (
                filteredOptions.map((option, index) => (
                    <InputDropdownSearchableOption
                        key={`${option.value}-${index}`}
                        id={`${id}-option-${index}`}
                        data-index={index}
                        role="option"
                        aria-selected={option.value === value}
                        $isActive={index === activeIndex}
                        $isSelected={option.value === value}
                        onMouseEnter={() => setActiveIndex(index)}
                        onClick={() => commit(option)}
                    >
                        {option.label}
                    </InputDropdownSearchableOption>
                ))
            )}
        </InputDropdownSearchablePanel>
    ) : null;

    const resolvedAlign = align === 'center' ? 'center' : 'left';

    return (
        <InputDropdownSearchableWrapper ref={wrapperRef} $size={size} $align={resolvedAlign}>
            {label && (
                <InputDropdownSearchableLabel $size={size} $align={resolvedAlign}>
                    {label}
                </InputDropdownSearchableLabel>
            )}
            <InputDropdownSearchableControl $size={size}>
                {isOpen ? (
                    <InputDropdownSearchableSearchField
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                        id={`${id}-search`}
                        role="combobox"
                        aria-expanded={true}
                        aria-controls={`${id}-listbox`}
                        aria-activedescendant={
                            filteredOptions.length > 0 ? `${id}-option-${activeIndex}` : undefined
                        }
                        autoComplete="off"
                        placeholder={searchPlaceholder}
                        value={query}
                        $size={size}
                        $align={resolvedAlign}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                    />
                ) : (
                    <InputDropdownSearchableTrigger
                        ref={triggerRef}
                        id={id}
                        role="combobox"
                        aria-expanded={false}
                        aria-haspopup="listbox"
                        tabIndex={0}
                        $size={size}
                        $align={resolvedAlign}
                        $isPlaceholder={!hasValue}
                        onClick={open}
                        onKeyDown={handleTriggerKeyDown}
                    >
                        {hasValue ? (selectedLabel ?? value) : placeholder}
                    </InputDropdownSearchableTrigger>
                )}
                <InputDropdownSearchableIcon $size={size}>
                    <KeyboardArrowDown />
                </InputDropdownSearchableIcon>
            </InputDropdownSearchableControl>

            {portalPanel ? (panel && createPortal(panel, document.body)) : panel}
        </InputDropdownSearchableWrapper>
    );
}
